<script setup lang="ts">
/**
 * Skeleton para seções com swiper (usado em Missions e MiniGames na tab "Todos")
 * Imita o layout do PerfilOficialSwiper com header + cards horizontais
 * Uso: <SmarticoApostatudoSkeletonSwiperSectionSkeleton :sections="3" :cards-per-section="4" />
 */

interface Props {
  /** Quantidade de seções skeleton */
  sections?: number;
  /** Quantidade de cards por seção */
  cardsPerSection?: number;
  /** Altura do card em pixels (desktop) */
  cardHeight?: number;
  /** Altura do card em pixels (mobile) */
  cardHeightMobile?: number;
  /** Largura mínima do card em pixels (desktop) */
  cardMinWidth?: number;
  /** Largura mínima do card em pixels (mobile) */
  cardMinWidthMobile?: number;
}

withDefaults(defineProps<Props>(), {
  sections: 3,
  cardsPerSection: 4,
  cardHeight: 200,
  cardHeightMobile: 150,
  cardMinWidth: 240,
  cardMinWidthMobile: 160,
});
</script>

<template>
  <div class="swiper-sections-skeleton">
    <div
      v-for="s in sections"
      :key="`section-${s}`"
      class="skeleton-section"
    >
      <!-- Header da seção (igual ao PerfilOficialSwiperHeader) -->
      <div class="skeleton-header">
        <div class="skeleton-header-left">
          <div class="skeleton-icon" />
          <div class="skeleton-title" />
        </div>
        <div class="skeleton-header-right">
          <div class="skeleton-see-more" />
          <div class="skeleton-arrows">
            <div class="skeleton-arrow" />
            <div class="skeleton-arrow" />
          </div>
        </div>
      </div>

      <!-- Swiper content (cards horizontais) -->
      <div class="skeleton-swiper-content">
        <div class="skeleton-swiper-items">
          <div
            v-for="c in cardsPerSection"
            :key="`card-${s}-${c}`"
            class="skeleton-card"
            :style="{
              '--card-height': `${cardHeight}px`,
              '--card-height-mobile': `${cardHeightMobile}px`,
              '--card-min-width': `${cardMinWidth}px`,
              '--card-min-width-mobile': `${cardMinWidthMobile}px`,
            }"
          >
            <!-- Conteúdo interno do card -->
            <div class="skeleton-card-image" />
            <div class="skeleton-card-info">
              <div class="skeleton-card-title" />
              <div class="skeleton-card-subtitle" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.swiper-sections-skeleton {
  @apply flex flex-col gap-4;
}

.skeleton-section {
  @apply flex flex-col;
}

/* Header */
.skeleton-header {
  @apply flex items-center justify-between mb-2 sm:mb-3;
  height: 32px;
  background: linear-gradient(
    270deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  border-radius: 6px;
}

@media (min-width: 640px) {
  .skeleton-header {
    height: 45px;
  }
}

.skeleton-header-left {
  @apply flex items-center gap-2 sm:gap-4;
}

.skeleton-icon {
  @apply rounded-lg;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-icon {
    width: 45px;
    height: 45px;
  }
}

.skeleton-title {
  @apply rounded;
  height: 14px;
  width: 80px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.1s;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-title {
    height: 20px;
    width: 128px;
  }
}

.skeleton-header-right {
  @apply flex items-center gap-3;
}

.skeleton-see-more {
  @apply h-4 w-20 rounded hidden sm:block;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
  will-change: opacity;
}

.skeleton-arrows {
  @apply hidden sm:flex gap-2;
}

.skeleton-arrow {
  @apply rounded-full;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.15s;
  will-change: opacity;
}

@media (min-width: 640px) {
  .skeleton-arrow {
    width: 32px;
    height: 32px;
  }
}

/* Swiper content */
.skeleton-swiper-content {
  @apply overflow-hidden;
}

.skeleton-swiper-items {
  @apply flex gap-2;
  min-height: 100px;
}

.skeleton-card {
  @apply flex-shrink-0 rounded-xl overflow-hidden flex flex-col;
  background: rgba(255, 255, 255, 0.03);
  height: var(--card-height-mobile);
  min-width: var(--card-min-width-mobile);
}

@media (min-width: 640px) {
  .skeleton-card {
    height: var(--card-height);
    min-width: var(--card-min-width);
  }
}

.skeleton-card-image {
  @apply flex-1;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  will-change: opacity;
}

.skeleton-card-info {
  @apply p-3 flex flex-col gap-2;
}

.skeleton-card-title {
  @apply h-4 w-3/4 rounded;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.1s;
  will-change: opacity;
}

.skeleton-card-subtitle {
  @apply h-3 w-1/2 rounded;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
  will-change: opacity;
}

/* Animação de pulse usando opacity (GPU-accelerated) */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* Mobile: cards menores */
@media (max-width: 640px) {
  .skeleton-swiper-items {
    @apply gap-2;
  }
}
</style>
